/* Admin-only Directory Snapshots page (founder-approved 2026-10-17).
   Lists monthly + on-demand snapshots, shows reconciliation status, and
   provides one-click CSV/JSON download. Guarded by AdminGate (administrator
   role); all API calls carry the user JWT. */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Camera, Download, Loader2, CheckCircle2, AlertTriangle, HardDrive, Clock } from "lucide-react";
import { AdminGate, AdminShell, styles } from "@/pages/AdminWorkspace";
import { useUserAuth } from "@/context/UserAuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ValidationBadge({ snap }) {
  const v = snap.validation;
  if (!v) {
    return <span className={`${styles.chip} bg-slate-100 text-slate-600`} data-testid={`snapshot-legacy-${snap.id}`}>Legacy — predates validation</span>;
  }
  if (v.passed) {
    return (
      <span className={`${styles.chip} bg-emerald-100 text-emerald-700`} data-testid={`snapshot-valid-${snap.id}`}>
        <CheckCircle2 className="w-3 h-3" /> Validated · {v.actual.total} records ({v.actual.public} public + {v.actual.admin_only} admin-only)
      </span>
    );
  }
  return (
    <span className={`${styles.chip} bg-amber-100 text-amber-800`} title={v.discrepancies.join(" · ")} data-testid={`snapshot-discrepancy-${snap.id}`}>
      <AlertTriangle className="w-3 h-3" /> Discrepancy — {v.discrepancies.join(" · ")}
    </span>
  );
}

function SnapshotsInner() {
  const { authHeaders } = useUserAuth();
  const [snaps, setSnaps] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/directory/snapshots`, { headers: authHeaders() })
      .then((r) => setSnaps(r.data.snapshots))
      .catch(() => toast.error("Unable to load snapshots"));
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/directory/snapshots`, {}, { headers: authHeaders() });
      if (r.data.validated) toast.success(`Snapshot created and validated — ${r.data.total_count} records`);
      else toast.warning(`Snapshot created with discrepancies: ${(r.data.validation?.discrepancies || []).join(" · ")}`);
      load();
    } catch {
      toast.error("Snapshot creation failed");
    } finally {
      setCreating(false);
    }
  };

  const download = async (id, fmt) => {
    try {
      const r = await axios.get(`${API}/admin/directory/snapshots/${id}/download`, {
        headers: authHeaders(), params: { format: fmt }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `who-directory-snapshot-${id.slice(0, 8)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded — save it to WHO-controlled storage for a true offline copy`);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="snapshots-content">
      <div className={`${styles.card} p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
        <div>
          <h2 className="font-display font-bold text-navy text-lg">Directory snapshots</h2>
          <p className="text-sm text-slateSoft mt-1">
            A monthly snapshot is archived automatically on the 1st of each month. You can also create one now.
            Expected reconciliation: 42 approved records — 40 public + 2 admin-only strategic ecosystem entries.
          </p>
        </div>
        <button onClick={create} disabled={creating} className={styles.btnGold} data-testid="snapshot-create-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          New snapshot
        </button>
      </div>

      <div className={`${styles.card} p-4 flex items-start gap-3 border-l-4 border-gold`} data-testid="snapshot-storage-note">
        <HardDrive className="w-4 h-4 text-gold-deep mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slateSoft">
          Snapshots listed here are stored in WHO&rsquo;s Emergent cloud database. An internal snapshot alone is
          not an offline backup — use Download and save the file to WHO-controlled storage (your computer,
          WHO&rsquo;s cloud drive, or records system).
        </p>
      </div>

      {!snaps ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold-deep" /></div>
      ) : snaps.length === 0 ? (
        <div className={`${styles.card} p-8 text-center text-slateSoft text-sm`} data-testid="snapshots-empty">
          No snapshots yet. Create the first one with &ldquo;New snapshot&rdquo;.
        </div>
      ) : (
        <div className={`${styles.card} divide-y divide-borderSubtle`} data-testid="snapshots-list">
          {snaps.map((s) => (
            <div key={s.id} className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 justify-between" data-testid={`snapshot-row-${s.id}`}>
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-navy flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-slateSoft" />
                  {new Date(s.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })} UTC
                  <span className="text-xs font-normal text-slateSoft">· {s.total_count} records · {s.trigger === "cron" ? "monthly automatic" : "manual"}</span>
                </p>
                <ValidationBadge snap={s} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => download(s.id, "csv")} className={styles.btnGhost} data-testid={`snapshot-download-csv-${s.id}`}>
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
                <button onClick={() => download(s.id, "json")} className={styles.btnGhost} data-testid={`snapshot-download-json-${s.id}`}>
                  <Download className="w-3.5 h-3.5" /> JSON
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSnapshots() {
  return (
    <AdminGate>
      <AdminShell title="Directory Snapshots" testid="admin-snapshots">
        <SnapshotsInner />
      </AdminShell>
    </AdminGate>
  );
}
